import React, { useState, useEffect, useRef } from 'react';
import './AddDoctorDialog.css';
import apiService from '../services/apiService';

const AddDoctorDialog = ({ isOpen, onClose, onDoctorAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    housenumber: '',
    postcode: '',
    city: '',
    healthcare: 'doctor',
    healthcareSpeciality: '',
    careerDuration: '',
    phone: '',
    website: '',
    openingHours: '',
    latitude: '',
    longitude: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [streetSearchResults, setStreetSearchResults] = useState([]);
  const [isStreetSearching, setIsStreetSearching] = useState(false);
  const streetDebounceTimer = useRef(null);
  const skipNextStreetSearch = useRef(false);

  useEffect(() => {
    if (skipNextStreetSearch.current) {
      skipNextStreetSearch.current = false;
      return;
    }

    if (formData.street.length >= 5) {
      if (streetDebounceTimer.current) {
        clearTimeout(streetDebounceTimer.current);
      }

      streetDebounceTimer.current = setTimeout(() => {
        performStreetSearch(formData.street);
      }, 300);
    } else {
      setStreetSearchResults([]);
      if (streetDebounceTimer.current) {
        clearTimeout(streetDebounceTimer.current);
      }
    }

    return () => {
      if (streetDebounceTimer.current) {
        clearTimeout(streetDebounceTimer.current);
      }
    };
  }, [formData.street]);

  const performStreetSearch = async (query) => {
    if (!query.trim()) return;

    setIsStreetSearching(true);
    try {
      const result = await apiService.searchLocation(query);
      if (result.results && result.results.length > 0) {
        setStreetSearchResults(result.results);
      } else {
        setStreetSearchResults([]);
      }
    } catch (err) {
      console.error('Street search failed:', err);
      setStreetSearchResults([]);
    } finally {
      setIsStreetSearching(false);
    }
  };

  const handleStreetResultSelect = (result) => {
    setFormData(prev => ({
      ...prev,
      latitude: result.latitude.toString(),
      longitude: result.longitude.toString(),
      city: result.properties?.city || prev.city,
      postcode: result.properties?.postcode || prev.postcode,
      street: result.properties?.street || result.name || prev.street,
      housenumber: result.properties?.housenumber || prev.housenumber
    }));
    skipNextStreetSearch.current = true;
    setStreetSearchResults([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    
    if (!formData.latitude || !formData.longitude) {
      setError('Koordinaten sind erforderlich. Bitte Adresse suchen oder manuell eingeben.');
      return;
    }

    setIsSubmitting(true);

    const doctorData = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(formData.longitude), parseFloat(formData.latitude)]
      },
      properties: {
        name: formData.name,
        'addr:street': formData.street,
        'addr:housenumber': formData.housenumber,
        'addr:postcode': formData.postcode,
        'addr:city': formData.city,
        healthcare: formData.healthcare,
        'healthcare:speciality': formData.healthcareSpeciality,
        'careerduration': formData.careerDuration,        
        phone: formData.phone,
        website: formData.website,
        opening_hours: formData.openingHours
      }
    };

    try {
      await apiService.createDoctor(doctorData);
      
      // Reset form
      setFormData({
        name: '',
        street: '',
        housenumber: '',
        postcode: '',
        city: '',
        healthcare: 'doctor',
        healthcareSpeciality: '',
        careerDuration: '',
        phone: '',
        website: '',
        openingHours: '',
        latitude: '',
        longitude: ''
      });
      
      if (onDoctorAdded) {
        onDoctorAdded();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern des Arztes');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Neuen Arzt hinzufügen</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="dialog-error">{error}</div>}
          
          <div className="form-section">
            <h3>Grunddaten</h3>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dr. Max Mustermann"
                className="form-input"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="healthcare">Typ</label>
                <select
                  id="healthcare"
                  name="healthcare"
                  value={formData.healthcare}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="doctor">Arzt</option>
                  <option value="dentist">Zahnarzt</option>
                  <option value="pharmacy">Apotheke</option>
                  <option value="hospital">Krankenhaus</option>
                  <option value="clinic">Klinik</option>
                  <option value="mvz">MVZ</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="healthcareSpeciality">Fachrichtung</label>
                <input
                  type="text"
                  id="healthcareSpeciality"
                  name="healthcareSpeciality"
                  value={formData.healthcareSpeciality}
                  onChange={handleChange}
                  placeholder="z.B. Allgemeinmedizin"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="careerDuration">Tätigkeitsdauer</label>
                <select
                  id="careerDuration"
                  name="careerDuration"
                  value={formData.careerDuration}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Bitte auswählen...</option>
                  <option value="more_than_five_years">Länger als fünf Jahre</option>
                  <option value="less_than_five_years">Kleiner als fünf Jahre</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Adresse</h3>
            <div className="form-row">
              <div className="form-group flex-3">
                <label htmlFor="street">Straße</label>
                <div className="address-search-container">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Straße eingeben (mind. 5 Zeichen)..."
                      className="form-input"
                    />
                    {isStreetSearching && <span className="search-spinner">🔍</span>}
                  </div>
                  {streetSearchResults.length > 0 && (
                    <div className="address-search-results">
                      {streetSearchResults.map((result, index) => (
                        <div
                          key={index}
                          className="address-result-item"
                          onClick={() => handleStreetResultSelect(result)}
                        >
                          <span className="result-name">{result.name}</span>
                          {(result.properties?.city || result.properties?.state) && (
                            <span className="result-location">
                              {[result.properties?.street, result.properties?.city, result.properties?.postcode]
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group flex-1">
                <label htmlFor="housenumber">Nr.</label>
                <input
                  type="text"
                  id="housenumber"
                  name="housenumber"
                  value={formData.housenumber}
                  onChange={handleChange}
                  placeholder="123"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group flex-1">
                <label htmlFor="postcode">PLZ</label>
                <input
                  type="text"
                  id="postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  placeholder="12345"
                  className="form-input"
                />
              </div>

              <div className="form-group flex-3">
                <label htmlFor="city">Stadt</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Musterstadt"
                  className="form-input"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Koordinaten</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="latitude">Breitengrad *</label>
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="51.1234"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="longitude">Längengrad *</label>
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="10.5678"
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Kontakt</h3>
            <div className="form-group">
              <label htmlFor="phone">Telefon</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+49 123 456789"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.example.de"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="openingHours">Öffnungszeiten</label>
              <input
                type="text"
                id="openingHours"
                name="openingHours"
                value={formData.openingHours}
                onChange={handleChange}
                placeholder="Mo-Fr 08:00-18:00"
                className="form-input"
              />
            </div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Abbrechen
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-submit">
              {isSubmitting ? 'Speichern...' : 'Arzt hinzufügen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDoctorDialog;
